import {
  blockPieceId,
  rehomeSpan,
  type LiveTemplate,
  type LiveTimeBlock,
  type TemplateRecord,
  type TimeBlockRecord,
} from '@wiztime/core';

/** A daily slot has no date; re-homing measures it from this fixed date (a Monday). */
export const TEMPLATE_REFERENCE_DATE = '2000-01-03';

/**
 * The block as it must be stored under the personal-day start `dayStartMin`, or null when it
 * already lies within its personal day (or is deleted).
 *
 * A block merged from a device that used another day start can lie partly or wholly outside the
 * personal day of its date, so the day views would not show it while statistics still counted it.
 * The block is cut at the day boundaries into pieces (see `rehomeSpan`). Every piece keeps the
 * timestamp and author of the block, so the re-homing is a pure function of the record and the
 * day start: each device derives the same pieces with the same identifiers, no device has to push
 * them, and any newer edit of the block or of a piece wins over them. The earliest piece keeps the
 * block's identifier; later pieces are named by their position (`blockPieceId`).
 */
export function rehomeToDay(block: TimeBlockRecord, dayStartMin: number): LiveTimeBlock[] | null {
  const data = block.data;
  if (block.deleted || data === null) return null;
  const pieces = rehomeSpan(data.personalDate, data.startMin, data.endMin, dayStartMin);
  const [only] = pieces;
  if (
    pieces.length === 1 &&
    only?.personalDate === data.personalDate &&
    only.startMin === data.startMin &&
    only.endMin === data.endMin
  ) {
    return null;
  }
  return pieces.map((piece, index) => ({
    ...block,
    id: index === 0 ? block.id : blockPieceId(block.id, piece.personalDate, piece.startMin, piece.endMin),
    deleted: false,
    data: { ...data, ...piece },
  })) as LiveTimeBlock[];
}

/**
 * The daily-slot template as it must be stored under the day start `dayStartMin`, or null when it does
 * not cross a day boundary (or is deleted). Same rules as `rehomeToDay`: the earliest piece keeps the
 * template's identifier, later pieces are named by position (`blockPieceId` on the reference date), every piece keeps the
 * timestamp and author of the template.
 */
export function rehomeTemplateToDay(template: TemplateRecord, dayStartMin: number): LiveTemplate[] | null {
  const data = template.data;
  if (template.deleted || data === null) return null;
  const pieces = rehomeSpan(TEMPLATE_REFERENCE_DATE, data.startMin, data.endMin, dayStartMin);
  const [only] = pieces;
  if (
    pieces.length === 1 &&
    only?.personalDate === TEMPLATE_REFERENCE_DATE &&
    only.startMin === data.startMin &&
    only.endMin === data.endMin
  ) {
    return null;
  }
  return pieces.map((piece, index) => ({
    ...template,
    id: index === 0 ? template.id : blockPieceId(template.id, TEMPLATE_REFERENCE_DATE, piece.startMin, piece.endMin),
    deleted: false,
    data: { ...data, startMin: piece.startMin, endMin: piece.endMin },
  })) as LiveTemplate[];
}
